/**
 * Feeds the header task dropdown (the Angular `iioProgress` directive in
 * partials/progress.html) from React.
 *
 * The dropdown tracks "monitorables" and is driven entirely by two $rootScope events that
 * the Angular `Service` base class used to emit from its websocket listeners. A migrated
 * page bypasses those services, so nothing reaches the dropdown unless React emits the
 * events itself. Angular services that are still instantiated emit their own events for
 * the same ids; the dropdown keys tasks by id, so an overlap updates one row rather than
 * creating two.
 *
 * Remove this once the header itself is React and owns task state directly.
 */
import { broadcastAngularEvent } from './angular-bridge';

export interface TaskProgress {
  id: string;
  /** Row label. The dropdown prefers job.description and falls back to name. */
  name: string;
  percentageComplete: number;
  description?: string;
  /** The dropdown orders rows by this. */
  createdAt?: number;
}

/**
 * Reports progress for a running task. Updates at or above 100% are dropped, mirroring the
 * Angular base class: a finished task is removed by reportTaskComplete, and emitting 100%
 * first would otherwise add a full bar that lingers if the completion event never arrives.
 */
export function reportTaskProgress(task: TaskProgress): void {
  if (!task.id || !isFinite(task.percentageComplete) || task.percentageComplete >= 100) return;

  broadcastAngularEvent('MonitorableUpdated', {
    id: task.id,
    name: task.name,
    createdAt: task.createdAt,
    job: {
      percentageComplete: task.percentageComplete,
      description: task.description,
    },
  });
}

/** Removes a task from the dropdown. */
export function reportTaskComplete(id: string | undefined | null): void {
  if (!id) return;
  broadcastAngularEvent('MonitorableCompleted', { id });
}
